import { FirmAnnouncementPopup } from './FirmAnnouncementPopup';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { level, useStarColors } from './five-star/stars';
import { useAuth } from '../context/AuthContext';
import { 
  LayoutDashboard, 
  MapPin, 
  Building2, 
  Users, 
  ClipboardCheck,
  FileText,
  Download,
  LogOut,
  Menu,
  X,
  Mail,
  Shield,
  UserCircle,
  Settings,
  Truck,
  Briefcase,
  ListTree,
  Tags,
  BadgeCheck,
  Siren,
  Clock,
  Megaphone,
  BellRing,
  BookOpen,
  Store,
  Inbox,
  Star,
  Award,
  ListChecks,
  KeyRound,
  History,
} from 'lucide-react';
import { ImpersonationBanner } from './firm-access/ImpersonationBanner';
import { FirmAccessWatcher } from './firm-access/FirmAccessWatcher';
import { exitImpersonation } from '../lib/firmAccess';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { useState, useEffect } from 'react';
import axios from 'axios';
import { ConsentRequiredDialog } from './firm/ConsentRequiredDialog';
import { useFirmEvents, FirmPassiveScreen } from './firm/FirmPassive';
import { RouteMemory } from './RouteMemory';
import { useOnline } from '../lib/offline';
import { StoreFirstLoginPrompt } from './stores/StoreFirstLoginPrompt';
import { toast } from 'sonner';
import { WifiOff } from 'lucide-react';
import { FIRM_ROLE, REG_STATUS, FirmStatusPanel, ApprovedCelebrationDialog, PendingNoticeDialog, PendingBanner, useMyCompany, useBlockWhilePending } from './firm/FirmApproval';
import { useLicense } from '../lib/license';
import { useUpdateCheck } from '../lib/pwa';
import { PwaInstallButton, VersionBadge } from './PwaInstall';
import { SidebarNav, useMenuPrefs } from './layout/SidebarNav';
import { ROLE_LABELS as ROLE_BADGES } from '../lib/roleBadges';
import { MobileQuickBar } from './layout/MobileQuickBar';
import { MaintenanceBanner } from './layout/MaintenanceBanner';

const noopStatusChange = () => {};

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const navItems = [
  { path: '/', icon: LayoutDashboard, label: 'Dashboard', module: 'dashboard' },
  { path: '/locations', icon: MapPin, label: 'Lokasyonlar', module: 'locations' },
  { path: '/companies', icon: Building2, label: 'Firmalar', module: 'companies' },
  { path: '/employees', icon: Users, label: 'Personel', module: 'employees' },
  { path: '/equipment', icon: Truck, label: 'İş Ekipmanı', module: 'equipment' },
  { path: '/equipment-types', icon: Settings, label: 'Ekipman Türleri', module: 'equipment_types' },
  { path: '/work-permits', icon: ClipboardCheck, label: 'Çalışma İzinleri', module: 'work_permits', showBadge: true },
  { path: '/work-areas', icon: Briefcase, label: 'Çalışma Alanları', module: 'work_areas' },
  { path: '/job-types', icon: ListTree, label: 'İş Tipi / Çeşidi', module: 'work_areas' },
  { path: '/templates', icon: FileText, label: 'Eksiklik Şablonları', module: 'templates' },
  { path: '/reports', icon: Download, label: 'Raporlar', module: 'companies' },
  { path: '/email-settings', icon: Mail, label: 'E-posta Ayarları', module: 'settings' },
  { path: '/users', icon: Shield, label: 'Kullanıcı Yönetimi', module: 'users' },
  { path: '/isg-taahhutname', icon: FileText, label: 'İSG Taahhütnameleri', module: 'isg_commitments' },
  { path: '/dokumantasyon', icon: BookOpen, label: 'Dokümantasyon', module: 'doc_library' },
  { path: '/magazalar', icon: Store, label: 'Mağazalar', module: 'stores' },
  { path: '/five-star', icon: Star, label: 'Five Star Denetimi', module: 'five_star' },
  { path: '/firma-degerlendirme', icon: Award, label: 'Firma Değerlendirme', module: 'firm_evaluation' },
  { path: '/five-star-basliklar', icon: ListChecks, label: 'Five Star Başlıkları', module: 'five_star_criteria' },
  { path: '/company-statuses', icon: Tags, label: 'Firma Statüsü', module: 'company_statuses' },
  { path: '/ozel-durumlar', icon: Siren, label: 'Özel Durumlar', module: 'special_situations' },
  { path: '/talep-zaman-kurallari', icon: Clock, label: 'Talep Zaman Kuralları', module: 'time_rules' },
  { path: '/giris-duyurulari', icon: Megaphone, label: 'Giriş Sayfası Duyuruları', module: 'login_announcements' },
  { path: '/kullanici-duyurulari', icon: BellRing, label: 'Kullanıcı Duyuruları', module: 'user_announcements' },
  { path: '/system-settings', icon: Settings, label: 'Sistem Ayarları', module: 'system_settings' },
  { path: '/kullanim-kilavuzu', icon: BookOpen, label: 'Kullanım Kılavuzu', module: 'user_guide' },
];

const ROLE_LABELS = {
  super_admin: 'Süper Admin',
  location_manager: 'Lokasyon Yöneticisi',
  isg_expert: 'İSG Uzmanı',
  '2_onayci': '2. Onaycı',
  viewer: 'Görüntüleyici',
  user: 'Kullanıcı',
  firma_yetkilisi: 'Firma Yetkilisi'
};

const Layout = () => {
  const { user, logout, canView } = useAuth();
  const { hasAddon } = useLicense();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [menuPrefs, saveMenuPrefs] = useMenuPrefs();
  const [menuEditing, setMenuEditing] = useState(false);
  const [pendingPermitsCount, setPendingPermitsCount] = useState(0);
  const isFirm = user?.role === FIRM_ROLE;
  const imp = user?.impersonation;
  const [myCompany, reloadMyCompany] = useMyCompany(isFirm);
  const showFirmPanel = pathname !== '/firma-durumu' && myCompany?.registration_status !== 'approved';

  useFirmEvents(isFirm, reloadMyCompany);
  // İSG / yönetici: talep durumu değişince listeler anlık yenilenir
  useFirmEvents(!isFirm && !!user, noopStatusChange);
  const [pendingRegCount, setPendingRegCount] = useState(0);
  const [storeKvkkCount, setStoreKvkkCount] = useState(0);
  const firmBlocked = isFirm && myCompany?.registration_status !== 'approved';
  // Onay bekleyen duyuru sayısı (Duyurularım rozeti)
  const [annPending, setAnnPending] = useState(0);
  useEffect(() => {
    if (!isFirm) return;
    const load = () => axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/firm/announcements`).then(r => setAnnPending(r.data.filter(a => a.mode === 'ack').length)).catch(() => {});
    load();
    window.addEventListener('announcements:count', load);
    window.addEventListener('announcements:refresh', load);
    return () => { window.removeEventListener('announcements:count', load); window.removeEventListener('announcements:refresh', load); };
  }, [isFirm]);
  // Yeni / güncellenmiş doküman sayısı (Dokümanlar rozeti)
  const [docsNew, setDocsNew] = useState(0);
  useEffect(() => {
    if (!isFirm) return;
    const load = () => axios.get(`${API}/firm/documents`).then(r => setDocsNew(r.data.filter(d => d.is_new).length)).catch(() => {});
    load();
    window.addEventListener('docs:count', load);
    return () => window.removeEventListener('docs:count', load);
  }, [isFirm]);
  // Five Star: firma yıldızının rengi + görülmemiş yeni denetim sayısı
  const [fiveStar, setFiveStar] = useState({ avg: null, unseen: 0 });
  const starColors = useStarColors();
  // Mağaza: bugün ve yarın başlayacak çalışma sayısı (Gelen Çalışma Talepleri rozeti)
  const [inboxCount, setInboxCount] = useState(0);
  const isStore = isFirm && myCompany?.company_type === 'magaza';
  useUpdateCheck();
  const online = useOnline();
  const [offlineSeen, setOfflineSeen] = useState(false);
  useEffect(() => {
    if (!online) setOfflineSeen(true);
    else if (offlineSeen) { toast.success('İnternet bağlantısı geri geldi, veriler güncelleniyor'); setOfflineSeen(false); }
  }, [online]);
  const [panelOpen, setPanelOpen] = useState(false);
  const [pendingNotice, setPendingNotice] = useState(false);

  // Onay bekleyen firmaya girişte bir kez bilgi ver; "Tamam" deyince sağ panel açılır
  useEffect(() => {
    if (!myCompany) return;
    if (myCompany.registration_status === 'approved') { setPanelOpen(false); return; }
    const key = `firm-notice-${user?.id}`;
    if (!sessionStorage.getItem(key)) { sessionStorage.setItem(key, '1'); setPendingNotice(true); }
    else setPanelOpen(true);
  }, [myCompany?.registration_status, user?.id]);

  const [firmPermitCount, setFirmPermitCount] = useState(null);
  const [celebrate, setCelebrate] = useState(false);

  // Onaylanan firmaya, henüz izin talebi yoksa bir kez kutlama göster
  useEffect(() => {
    if (myCompany?.registration_status !== 'approved') return;
    axios.get(`${API}/work-permits?page_size=1`).then(res => {
      const total = res.data.total ?? 0;
      setFirmPermitCount(total);
      const key = `firm-approved-celebrated-${myCompany.id}`;
      if (!localStorage.getItem(key)) {
        localStorage.setItem(key, '1');
        if (total === 0) setCelebrate(true);
      }
    }).catch(() => {});
  }, [myCompany?.registration_status, myCompany?.id]);
  // Talep sayısı sayfa geçişlerinde değil, yalnız talep değişince (anlık bildirim) ve henüz talebi yokken tazelenir
  useEffect(() => {
    if (myCompany?.registration_status !== 'approved' || firmPermitCount === null || firmPermitCount > 0) return undefined;
    const reload = () => axios.get(`${API}/work-permits?page_size=1`).then(res => setFirmPermitCount(res.data.total ?? 0)).catch(() => {});
    window.addEventListener('firm-permit-status', reload);
    return () => window.removeEventListener('firm-permit-status', reload);
  }, [myCompany?.registration_status, firmPermitCount]);

  const blockClick = useBlockWhilePending(firmBlocked, () => setPendingNotice(true));

  // Menü rozetleri tek istekte (nav-counts). Güncelleme: anlık bildirim (talep/kayıt/belge/Five Star) + sekmeye dönüş;
  // kaçırılan olaylara karşı 2 dk yedek kontrol (sekme arka plandayken istek atılmaz)
  useEffect(() => {
    let timer = null;
    const load = () => axios.get(`${API}/nav-counts`).then(({ data }) => {
      setPendingPermitsCount(data.pending_permits || 0);
      setPendingRegCount(data.pending_registrations || 0);
      setStoreKvkkCount(data.store_pending || 0);
      setInboxCount(data.store_incoming || 0);
      if (data.five_star) setFiveStar(data.five_star);
    }).catch(() => {});
    const soon = () => { clearTimeout(timer); timer = setTimeout(() => { if (!document.hidden) load(); }, 400); };
    load();
    const interval = setInterval(() => { if (!document.hidden) load(); }, 120000);
    const onVisible = () => { if (!document.hidden) soon(); };
    const events = ['firm-permit-status', 'nav-counts', 'fivestar:seen'];
    events.forEach(e => window.addEventListener(e, soon));
    document.addEventListener('visibilitychange', onVisible);
    return () => { clearTimeout(timer); clearInterval(interval); events.forEach(e => window.removeEventListener(e, soon)); document.removeEventListener('visibilitychange', onVisible); };
  }, []);

  const handleLogout = () => {
    if (imp) { exitImpersonation(); return; }
    sessionStorage.removeItem('last-path');
    logout();
    navigate('/login');
  };

  // Kullanıcının görebileceği menüleri filtrele
  const firmNavItem = { path: '/firma-durumu', icon: BadgeCheck, label: 'Firma Onay Durumu', firmStatus: true };
  const firmAnnItem = { path: '/duyurularim', icon: BellRing, label: 'Duyurularım', annBadge: true };
  const firmDocsItem = { path: '/dokumanlar', icon: BookOpen, label: 'Dokümanlar', docsBadge: true };
  const firmGuideItem = { path: '/firma-kilavuzu', icon: BookOpen, label: 'Kullanım Kılavuzu' };
  const firmStarItem = { path: '/five-star-degerlendirme', icon: Star, label: 'Five Star Değerlendirme', starBadge: true };
  const storeInboxItem = { path: '/gelen-talepler', icon: Inbox, label: 'Gelen Çalışma Talepleri', inboxBadge: true };
  const fiveStarOn = hasAddon('five_star');
  const firmAccessItems = imp ? [] : [
    ...(user?.role === 'isg_expert' && user?.firm_access_enabled ? [{ path: '/firma-erisimi', icon: KeyRound, label: 'Firma Erişimi' }] : []),
    ...(user?.is_super_admin || isFirm ? [{ path: '/firma-erisim-gecmisi', icon: History, label: 'Firma Erişim Geçmişi' }] : []),
  ];
  const visibleNavItems = [...navItems.filter(item => {
    if (item.superAdminOnly) return user?.is_super_admin;
    return canView(item.module);
  }), ...(isFirm ? [firmNavItem, firmAnnItem, firmDocsItem, ...(myCompany?.company_type === 'magaza' || !fiveStarOn ? [] : [firmStarItem]), firmGuideItem] : []), ...(isFirm && myCompany?.company_type === 'magaza' ? [storeInboxItem] : []), ...firmAccessItems];

  const renderNavItem = (item) => (
              <NavLink
      key={item.path}
      to={item.path}
      end={item.path === '/'}
      onClick={() => setSidebarOpen(false)}
      className={({ isActive }) =>
        `relative flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all duration-150 ${
          isActive
            ? 'bg-slate-100 text-slate-900 font-medium'
            : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
        }`
      }
      data-testid={`nav-${item.path.replace('/', '') || 'dashboard'}`}
    >
      {({ isActive }) => (
        <>
          {isActive && (
            <span className="absolute left-0 top-0 bottom-0 w-1 bg-orange-500 rounded-r" />
          )}
          <item.icon
            className={`h-5 w-5 ${item.firmStatus && myCompany ? (myCompany.registration_status === 'approved' ? 'text-emerald-600' : 'text-rose-600') : ''} ${item.starBadge ? (fiveStar.avg ? 'fill-current' : 'text-slate-300') : ''}`}
            style={item.starBadge && fiveStar.avg ? { color: starColors[level(fiveStar.avg)] } : undefined}
            data-testid={item.firmStatus ? 'nav-firm-status-icon' : item.starBadge ? 'nav-five-star-icon' : undefined}
          />
          <span className="flex-1" title={item.firmStatus && myCompany ? (REG_STATUS[myCompany.registration_status] || REG_STATUS.pending).label : undefined}>{item.label}</span>
          {item.annBadge && annPending > 0 && <span className="min-w-[20px] rounded-full bg-rose-600 px-1.5 py-0.5 text-center text-[11px] font-bold leading-none text-white animate-in zoom-in" data-testid="nav-ann-badge">{annPending}</span>}
          {item.path === '/magazalar' && storeKvkkCount > 0 && <span className="min-w-[20px] rounded-full bg-amber-500 px-1.5 py-0.5 text-center text-[11px] font-bold leading-none text-white" title="KVKK onayı bekleyen mağaza" data-testid="nav-store-kvkk-badge">{storeKvkkCount}</span>}
          {item.inboxBadge && inboxCount > 0 && <span className="min-w-[20px] rounded-full bg-blue-600 px-1.5 py-0.5 text-center text-[11px] font-bold leading-none text-white animate-in zoom-in" title="Bugün ve yarın başlayacak çalışma" data-testid="nav-inbox-badge">{inboxCount}</span>}
          {item.starBadge && fiveStar.unseen > 0 && <span className="min-w-[20px] rounded-full bg-rose-600 px-1.5 py-0.5 text-center text-[11px] font-bold leading-none text-white animate-in zoom-in" title="Yeni Five Star denetimi" data-testid="nav-five-star-badge">{fiveStar.unseen}</span>}
          {item.docsBadge && docsNew > 0 && <span className="min-w-[20px] rounded-full bg-orange-500 px-1.5 py-0.5 text-center text-[11px] font-bold leading-none text-white animate-in zoom-in" title="Yeni / güncellenmiş doküman" data-testid="nav-docs-badge">{docsNew}</span>}
          {/* Bekleyen izin badge'i */}
          {item.path === '/companies' && pendingRegCount > 0 && (
            <span className="flex items-center justify-center min-w-5 h-5 px-1.5 text-xs font-bold text-white bg-amber-500 rounded-full" title="Onay bekleyen firma başvurusu / belge" data-testid="pending-registrations-badge">
              {pendingRegCount}
            </span>
          )}
          {item.showBadge && pendingPermitsCount > 0 && (
            <span className="flex items-center justify-center min-w-5 h-5 px-1.5 text-xs font-bold text-white bg-rose-500 rounded-full animate-pulse" data-testid="pending-permits-badge">
              {pendingPermitsCount}
            </span>
          )}
        </>
      )}
    </NavLink>
  );

  if (isFirm && myCompany?.is_passive) {
    return <FirmPassiveScreen company={myCompany} onLogout={handleLogout} />;
  }

  return (
    <div className={`min-h-screen bg-slate-50 ${imp ? 'pt-10' : ''}`}>
      {imp && <ImpersonationBanner info={imp} />}
      {/* Mobile menu button */}
      <header className={`lg:hidden fixed ${imp ? 'top-10' : 'top-0'} inset-x-0 z-50 h-14 bg-white border-b border-slate-200 flex items-center px-3`} data-testid="mobile-top-bar">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setSidebarOpen(!sidebarOpen)}
          data-testid="mobile-menu-btn"
        >
          {sidebarOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </Button>
        <span className="absolute left-1/2 -translate-x-1/2 font-bold text-slate-900 tracking-tight" data-testid="mobile-top-bar-title">AVM Belge Takip <VersionBadge testId="mobile-app-version" /></span>
      </header>

      {/* Sidebar */}
      <aside 
        className={`fixed ${imp ? 'top-24 lg:top-10' : 'top-14 lg:top-0'} bottom-16 lg:bottom-0 left-0 z-40 bg-white border-r border-slate-200 transform transition-[transform,width,box-shadow] duration-200 ease-in-out ${menuEditing ? 'w-80 shadow-2xl' : 'w-64'} lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-6 border-b border-slate-200">
            <h1 className="flex items-center gap-2 text-xl font-bold text-slate-900 tracking-tight">
              AVM Belge Takip <VersionBadge />
            </h1>
            <p className="text-xs text-slate-500 mt-1">İSG Yönetim Sistemi</p>
            <PwaInstallButton />
          </div>

          {/* Navigation */}
          <SidebarNav items={visibleNavItems} renderItem={renderNavItem} prefs={menuPrefs} save={saveMenuPrefs} userId={user?.id} onEditChange={setMenuEditing} searchable />

          {/* User section */}
          <div className="p-4 border-t border-slate-200">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-9 h-9 rounded-full bg-slate-200 flex items-center justify-center">
                <span className="text-sm font-medium text-slate-600">
                  {user?.name?.charAt(0).toUpperCase()}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-900 truncate">{user?.name}</p>
                <p className="text-xs text-slate-500 truncate">{user?.email}</p>
              </div>
            </div>
            {isFirm && myCompany?.name && (
              <div className="mb-3 flex items-center gap-1.5 rounded-md bg-orange-50 px-2 py-1.5 text-xs font-semibold text-orange-800" title={myCompany.name} data-testid="sidebar-company-name">
                {isStore ? <Store className="h-3.5 w-3.5 shrink-0" /> : <Building2 className="h-3.5 w-3.5 shrink-0" />}
                <span className="truncate"><span className="font-medium text-orange-700">{isStore ? 'Mağaza adı' : 'Firma adı'}:</span> {myCompany.name}</span>
              </div>
            )}
            {user?.role && (() => {
              const rb = ROLE_BADGES[isFirm && isStore ? 'magaza_yetkilisi' : user.role] || { label: ROLE_LABELS[user.role] || user.role, color: 'bg-slate-100 text-slate-700' };
              return (
                <div className="mb-3 flex items-center gap-1.5 px-1" data-testid="sidebar-user-role">
                  <span className="text-xs text-slate-500">Rol:</span>
                  <span className={`inline-flex items-center rounded-md px-2 py-0.5 text-xs font-semibold ${rb.color}`}>
                    {user.is_super_admin && <Shield className="h-3 w-3 mr-1" />}{rb.label}
                  </span>
                </div>
              );
            })()}
            <div className="space-y-2">
              {!imp && <Button
                variant="outline"
                className="w-full justify-start gap-2"
                onClick={() => {
                  navigate('/profile');
                  setSidebarOpen(false);
                }}
                data-testid="profile-btn"
              >
                <UserCircle className="h-4 w-4" />
                Profilim
              </Button>}
              <Button
                variant="outline"
                className="w-full justify-start gap-2"
                onClick={handleLogout}
                data-testid="logout-btn"
              >
                <LogOut className="h-4 w-4" />
                {imp ? 'Firma Erişiminden Çık' : 'Cikis Yap'}
              </Button>
            </div>
          </div>
        </div>
      </aside>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div
          className={`fixed inset-x-0 ${imp ? 'top-24' : 'top-14'} bottom-16 bg-black/20 z-30 lg:hidden`}
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <MobileQuickBar items={visibleNavItems} prefs={menuPrefs} onMenu={() => setSidebarOpen(o => !o)} onNavigate={() => setSidebarOpen(false)} />

      {/* Main content */}
      <RouteMemory />
      <main className={`pt-14 pb-16 lg:pb-0 lg:pt-0 lg:ml-64 min-h-screen min-w-0 transition-[margin] duration-200 ${isFirm && panelOpen && showFirmPanel ? 'lg:mr-72' : ''}`}>
        <div className="p-3 sm:p-6 lg:p-8" onClickCapture={blockClick} onPointerDownCapture={blockClick}>
          <MaintenanceBanner />
          {!online && (
            <div className="mb-5 flex items-start gap-3 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900" data-testid="offline-banner">
              <WifiOff className="h-5 w-5 shrink-0 text-amber-600" />
              <span><b>İnternet bağlantısı yok.</b> {isFirm ? 'Bağlantı gelene kadar işlem yapılamaz.' : 'Çevrimdışı moddasınız: Çalışma İzinleri sayfasında cihazınıza kaydedilmiş onaylı talepleri görüntüleyebilirsiniz. Yeni işlem veya değişiklik yapılamaz; bağlantı gelince veriler otomatik güncellenir.'}</span>
              {!isFirm && pathname !== '/work-permits' && <button type="button" onClick={() => navigate('/work-permits')} className="ml-auto shrink-0 font-semibold underline" data-testid="offline-go-permits">Onaylı taleplere git</button>}
            </div>
          )}
          {isFirm && myCompany && <PendingBanner status={myCompany.registration_status} />}
          {isFirm && !imp && <FirmAccessWatcher />}
          <Outlet context={{ myCompany, reloadMyCompany, firmPermitCount }} />
        </div>
      </main>

      {isFirm && (
        <>
          {showFirmPanel && <FirmStatusPanel company={myCompany} open={panelOpen} onToggle={() => setPanelOpen(o => !o)} onChanged={reloadMyCompany} permitCount={firmPermitCount} />}
          <ConsentRequiredDialog open={!imp && !!myCompany?.consent_required} onAccepted={reloadMyCompany} />
          <FirmAnnouncementPopup enabled={!imp && !!myCompany && !myCompany.consent_required && !celebrate} />
          {!imp && myCompany?.company_type === 'magaza' && !myCompany.consent_required && <StoreFirstLoginPrompt userId={user?.id} />}
          <ApprovedCelebrationDialog open={!imp && celebrate && !myCompany?.consent_required && myCompany?.company_type !== 'magaza'} onClose={() => setCelebrate(false)} companyName={myCompany?.name} />
          <PendingNoticeDialog open={!imp && pendingNotice && !myCompany?.consent_required} onClose={() => {
            setPendingNotice(false);
            setPanelOpen(true);
            if (window.innerWidth < 1024 && pathname !== '/firma-durumu') navigate('/firma-durumu');
          }} />
        </>
      )}
    </div>
  );
};

export default Layout;
